import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription, finalize, interval, startWith, switchMap, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { CompensationRegularMaximumSettingService } from 'src/app/services/api/teach-compensation/compensation-regular-maximum-setting.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { CompensationRegularMaximumSettingModel } from 'src/app/services/models/teach-compensation/compensation-regular-maximum-setting.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { InputComponent } from 'src/shared/input/input.component';
import { SelectComponent } from 'src/shared/select/select.component';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  currentAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';

@Component({
  selector: 'app-compensation-rates',
  imports: [CommonModule, ReactiveFormsModule, InputComponent, SelectComponent, ConfirmDialogComponent],
  templateUrl: './compensation-rates.component.html',
  styleUrl: './compensation-rates.component.scss',
})
export class CompensationRatesComponent implements OnInit, OnDestroy {
  @ViewChild('saveConfirmDialog') saveConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('saveSuccessDialog') saveSuccessDialog?: ConfirmDialogComponent;

  readonly form = inject(FormBuilder).group({
    academicTermId: [null as number | null, Validators.required],
    academicTerm: [null as string | null, Validators.required],
    academicYear: [null as string | null, Validators.required],
    regularMaxAmount: [null as number | null, [Validators.required, Validators.min(0.01)]],
  });

  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];
  setting = signal<CompensationRegularMaximumSettingModel | null>(null);
  loading = signal(false);
  saving = signal(false);
  loadError = signal('');

  private academicTerms: AcademicTermYearModel[] = [];
  private readonly subscriptions = new Subscription();
  private pollSubscription?: Subscription;
  private readonly settingService = inject(CompensationRegularMaximumSettingService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);

  get recalculationActive(): boolean {
    return ['queued', 'running'].includes(this.setting()?.latest_recalculation?.status ?? '');
  }

  get settingLocked(): boolean {
    return this.setting()?.locked === true;
  }

  get settingLockedMessage(): string {
    if (this.setting()?.locked_reason === 'external_source_published') {
      return 'ข้อมูล ERP หรือข้อมูลไปปฏิบัติงานถูก Public แล้ว ไม่สามารถแก้ไขอัตราเบิกจ่ายของเทอมนี้ได้';
    }

    return 'ถึงวันที่ส่งเบิกแล้ว ไม่สามารถแก้ไขอัตราเบิกจ่ายของเทอมนี้ได้';
  }

  ngOnInit(): void {
    this.listenTermFilters();
    this.loadAcademicTerms();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.pollSubscription?.unsubscribe();
  }

  openSaveConfirm(): void {
    this.form.controls.regularMaxAmount.markAsTouched();
    if (this.form.invalid || this.saving() || this.recalculationActive || this.settingLocked) {
      return;
    }
    this.saveConfirmDialog?.open();
  }

  save(): void {
    const { academicTermId, regularMaxAmount } = this.form.getRawValue();
    if (
      academicTermId == null
      || regularMaxAmount == null
      || this.form.invalid
      || this.saving()
      || this.settingLocked
    ) {
      return;
    }

    this.saving.set(true);
    this.settingService.updateSetting(academicTermId, { regular_max_amount: Number(regularMaxAmount) }).pipe(
      take(1),
      finalize(() => this.saving.set(false)),
    ).subscribe({
      next: (setting) => {
        this.saveConfirmDialog?.close();
        this.applySetting(setting);
        this.saveSuccessDialog?.open();
      },
      error: (error) => {
        this.saveConfirmDialog?.close();
        this.toast.toastError(this.errorMessage(error), 3500);
      },
    });
  }

  private listenTermFilters(): void {
    this.subscriptions.add(
      this.form.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.form.controls.academicTerm.valueChanges.subscribe(() => {
        this.syncAcademicTermId();
        this.loadSetting();
      }),
    );
  }

  private loadAcademicTerms(): void {
    this.loading.set(true);
    this.studyPeriodService.getAcademicTermYears().pipe(
      take(1),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const selected = currentAcademicTerm(terms, this.authService.currentUser()?.academic_term);
        const year = selected == null ? null : String(toBuddhistYear(selected.year));
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, year);
        this.form.patchValue({
          academicTermId: selected?.id ?? null,
          academicTerm: selected == null ? null : String(selected.name).trim(),
          academicYear: year,
        }, { emitEvent: false });
        this.loadSetting();
      },
      error: () => this.loadError.set('ไม่สามารถโหลดรายการภาคการศึกษาได้'),
    });
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.form.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.form.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
    this.loadSetting();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.form.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.form.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
  }

  private loadSetting(): void {
    const academicTermId = this.form.getRawValue().academicTermId;
    this.stopPolling();
    if (academicTermId == null) {
      this.setting.set(null);
      return;
    }

    this.loading.set(true);
    this.loadError.set('');
    this.settingService.getSetting(academicTermId).pipe(
      take(1),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: (setting) => this.applySetting(setting),
      error: () => this.loadError.set('ไม่สามารถโหลดการตั้งค่าวงเงินได้'),
    });
  }

  private applySetting(setting: CompensationRegularMaximumSettingModel): void {
    this.setting.set(setting);
    this.form.patchValue({ regularMaxAmount: setting.regular_max_amount }, { emitEvent: false });
    this.updateAmountAvailability();
    if (this.recalculationActive) {
      this.startPolling(setting.academic_term_id);
    } else {
      this.stopPolling();
    }
  }

  private startPolling(academicTermId: number): void {
    this.stopPolling();
    this.pollSubscription = interval(3000).pipe(
      startWith(0),
      switchMap(() => this.settingService.getSetting(academicTermId)),
    ).subscribe({
      next: (setting) => {
        this.setting.set(setting);
        this.updateAmountAvailability();
        if (!['queued', 'running'].includes(setting.latest_recalculation?.status ?? '')) {
          this.stopPolling();
        }
      },
      error: () => this.stopPolling(),
    });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }

  private updateAmountAvailability(): void {
    if (this.settingLocked || this.recalculationActive) {
      this.form.controls.regularMaxAmount.disable({ emitEvent: false });
      return;
    }

    this.form.controls.regularMaxAmount.enable({ emitEvent: false });
  }

  private errorMessage(error: any): string {
    const messages = error?.error?.errors;
    return Array.isArray(messages) && messages.length > 0
      ? messages.join(' ')
      : 'ไม่สามารถบันทึกวงเงินได้ กรุณาลองใหม่อีกครั้ง';
  }
}
